// // Library methods
import { useTranslation } from "react-i18next";
import { useMemo } from "react";

// Components
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";

// Types
import { IndividualExternalExamType } from "src/services/exams/types";

// Utils
import {
  formatIndexAsPercentage,
  toHumanReadable,
} from "src/utils/exams/legacyExamHelper";

type LegacyVFDetailsType = {
  vfExam: IndividualExternalExamType;
};

const LegacyVFDetails = ({ vfExam }: LegacyVFDetailsType) => {
  const { t } = useTranslation();

  const hasDefectAnalysis = useMemo(
    () =>
      vfExam?.data?.patternDefect ||
      vfExam?.data?.overallDefect ||
      (vfExam?.data?.clusterAnalysis &&
        vfExam?.data?.clusterAnalysis !== "N/A"),
    [
      vfExam?.data?.patternDefect,
      vfExam?.data?.overallDefect,
      vfExam?.data?.clusterAnalysis,
    ]
  );

  return (
    <Grid display="flex" flexDirection="column" gap="20px">
      <Box display="flex" gap="8px">
        <Typography fontWeight="bold" minWidth="fit-content" fontSize="14px">
          {t("Machine Source")}:
        </Typography>
        <Typography fontSize="14px">
          {vfExam?.data?.machineSource ?? "N/A"}
        </Typography>
      </Box>

      <Box display="flex" flexDirection="column" gap="8px">
        <Typography fontWeight="bold" fontSize="14px">
          {t("Indices")}
        </Typography>
        <Box
          display="flex"
          sx={{
            flexDirection: { xs: "column", sm: "row" },
            border: "1px solid black",
            borderRadius: "4px",
            padding: "12px",
            gap: "20px",
          }}
        >
          <Grid
            container
            item
            sx={{
              display: "grid",
              gap: "4px",
              gridTemplateColumns: {
                xs: "repeat(1, 1fr)",
                sm: "repeat(2, 1fr)",
              },
            }}
          >
            <Box display="flex" gap="8px">
              <Typography fontWeight="bold" fontSize="14px">
                {t("MD")}:
              </Typography>
              <Typography fontSize="14px">
                {vfExam?.data?.meanDeviation ?? "N/A"}
              </Typography>
            </Box>

            <Box display="flex" gap="8px">
              <Typography fontWeight="bold" fontSize="14px">
                {t("PSD")}:
              </Typography>
              <Typography fontSize="14px">
                {vfExam?.data?.patternStandardDeviation ?? "N/A"}
              </Typography>
            </Box>

            {formatIndexAsPercentage(vfExam?.data?.visualFieldIndex) !==
              "N/A" &&
              vfExam?.data?.visualFieldIndex >= 0 && (
                <Box display="flex" gap="8px">
                  <Typography fontWeight="bold" fontSize="14px">
                    {t("VFI")}:
                  </Typography>
                  <Typography fontSize="14px">
                    {formatIndexAsPercentage(vfExam?.data?.visualFieldIndex)}
                  </Typography>
                </Box>
              )}

            {vfExam?.data?.glaucomaHemifieldTest !== "Undefined" && (
              <Box display="flex" gap="8px">
                <Typography fontWeight="bold" fontSize="14px">
                  {t("GHT")}:
                </Typography>
                <Typography fontSize="14px">
                  {t(toHumanReadable(vfExam?.data?.glaucomaHemifieldTest))}
                </Typography>
              </Box>
            )}

            {vfExam?.data?.foveaDb !== "N/A" && (
              <Box display="flex" gap="8px">
                <Typography fontWeight="bold" fontSize="14px">
                  {t("Fovea DB")}:
                </Typography>
                <Typography fontSize="14px">{vfExam?.data?.foveaDb}</Typography>
              </Box>
            )}
          </Grid>
        </Box>
      </Box>

      {hasDefectAnalysis && (
        <Box display="flex" flexDirection="column" gap="8px">
          <Typography fontWeight="bold" fontSize="14px">
            {t("Defect Analysis")}
          </Typography>
          <Box
            display="flex"
            sx={{
              flexDirection: { xs: "column", sm: "row" },
              border: "1px solid black",
              borderRadius: "4px",
              padding: "12px",
              gap: "20px",
            }}
          >
            <Grid
              container
              item
              sx={{
                display: "grid",
                gap: "4px",
                gridTemplateColumns: {
                  xs: "repeat(1, 1fr)",
                  sm: "repeat(2, 1fr)",
                },
              }}
            >
              {vfExam?.data?.patternDefect && (
                <Box display="flex" gap="8px">
                  <Typography fontWeight="bold" fontSize="14px">
                    {t("Pattern Defect")}:
                  </Typography>
                  <Typography fontSize="14px">
                    {vfExam?.data?.patternDefect?.toFixed(1)}
                  </Typography>
                </Box>
              )}

              {vfExam?.data?.overallDefect && (
                <Box display="flex" gap="8px">
                  <Typography fontWeight="bold" fontSize="14px">
                    {t("Overall Defect")}:
                  </Typography>
                  <Typography fontSize="14px">
                    {vfExam?.data?.overallDefect?.toFixed(1)}
                  </Typography>
                </Box>
              )}

              {vfExam?.data?.clusterAnalysis &&
                vfExam?.data?.clusterAnalysis !== "N/A" && (
                  <Box display="flex" gap="8px">
                    <Typography fontWeight="bold" fontSize="14px">
                      {t("Cluster Analysis")}:
                    </Typography>
                    <Typography fontSize="14px">
                      {vfExam?.data?.clusterAnalysis}
                    </Typography>
                  </Box>
                )}
            </Grid>
          </Box>
        </Box>
      )}
    </Grid>
  );
};

export default LegacyVFDetails;
